import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { timeEntriesApi } from "../services/api";
import { useUserDataContext } from "../context/UserDataContext";
import FloatingTimer from "../components/ui/FloatingTimer";

// ── Types ────────────────────────────────────────────────────────────

export interface RunningTimerEntry {
  _id: string;
  matterId: { _id: string; title: string; matterNumber?: string } | string | null;
  clientId: { _id: string; displayName?: string } | string | null;
  narrative: string;
  rateCents: number;
  billable: boolean;
  timer: {
    startedAt: string;
    stoppedAt: string | null;
    isRunning: boolean;
  };
}

export interface StoppedTimerEntry {
  _id: string;
  matterId: { _id: string; title: string; matterNumber?: string } | string | null;
  clientId: { _id: string; displayName?: string } | string | null;
  narrative: string;
  rateCents: number;
  billable: boolean;
  minutes: number;
  amountCents: number;
  timer: {
    startedAt: string;
    stoppedAt: string;
    isRunning: boolean;
  };
}

interface RunningTimerContextValue {
  /** The currently running timer entry, or null */
  runningTimer: RunningTimerEntry | null;
  /** The most recently stopped timer entry (for review), or null */
  stoppedEntry: StoppedTimerEntry | null;
  /** Elapsed seconds since the timer started */
  elapsedSeconds: number;
  /** Start a new timer */
  startTimer: (data: {
    matterId?: string | null;
    clientId?: string | null;
    narrative?: string;
    rateCents: number;
    billable?: boolean;
  }) => Promise<void>;
  /** Stop the running timer — returns stopped entry for review */
  stopTimer: () => Promise<StoppedTimerEntry | null>;
  /** Update the running timer (e.g. assign a matter mid-run) */
  updateRunningTimer: (
    data: { matterId?: string; clientId?: string; narrative?: string }
  ) => Promise<void>;
  /** Update the stopped entry (edit minutes, narrative, etc.) */
  updateStoppedEntry: (
    id: string,
    data: { minutes?: number; narrative?: string; rateCents?: number }
  ) => Promise<void>;
  /** Dismiss the stopped entry review */
  dismissStoppedEntry: () => void;
  /** Whether a start/stop operation is in progress */
  loading: boolean;
  /** Refresh running timer state from server */
  refresh: () => void;
}

const RunningTimerContext = createContext<RunningTimerContextValue | null>(null);

export const useRunningTimer = () => {
  const ctx = useContext(RunningTimerContext);
  if (!ctx)
    throw new Error(
      "useRunningTimer must be used within RunningTimerProvider"
    );
  return ctx;
};

// ── Provider ─────────────────────────────────────────────────────────

export const RunningTimerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { userData } = useUserDataContext();
  const [runningTimer, setRunningTimer] = useState<RunningTimerEntry | null>(
    null
  );
  const [stoppedEntry, setStoppedEntry] = useState<StoppedTimerEntry | null>(
    null
  );
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Calculate elapsed from startedAt ───────────────────────────────

  const calcElapsed = useCallback((startedAt: string) => {
    return Math.floor(
      (Date.now() - new Date(startedAt).getTime()) / 1000
    );
  }, []);

  // ── Tick interval ──────────────────────────────────────────────────

  useEffect(() => {
    if (runningTimer?.timer?.isRunning && runningTimer.timer.startedAt) {
      setElapsedSeconds(calcElapsed(runningTimer.timer.startedAt));
      intervalRef.current = setInterval(() => {
        setElapsedSeconds(calcElapsed(runningTimer.timer.startedAt));
      }, 1000);
    } else {
      setElapsedSeconds(0);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [runningTimer, calcElapsed]);

  // ── Fetch running timer on mount ───────────────────────────────────

  const fetchRunning = useCallback(async () => {
    try {
      const res = await timeEntriesApi.getRunning();
      setRunningTimer(res.data || null);
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    if (userData?._id) {
      fetchRunning();
    }
  }, [userData?._id, fetchRunning]);

  // ── Start timer ────────────────────────────────────────────────────

  const startTimer = useCallback(
    async (data: {
      matterId?: string | null;
      clientId?: string | null;
      narrative?: string;
      rateCents: number;
      billable?: boolean;
    }) => {
      setLoading(true);
      try {
        const res = await timeEntriesApi.startTimer({
          ...data,
          matterId: data.matterId || undefined,
          clientId: data.clientId || undefined,
          userId: userData._id,
          narrative: data.narrative || "Timer in progress",
          billable: data.billable ?? true,
        });
        setRunningTimer(res.data);
        setStoppedEntry(null);
      } finally {
        setLoading(false);
      }
    },
    [userData?._id]
  );

  // ── Stop timer ─────────────────────────────────────────────────────

  const stopTimer = useCallback(async (): Promise<StoppedTimerEntry | null> => {
    if (!runningTimer) return null;
    setLoading(true);
    try {
      const res = await timeEntriesApi.stopTimer(runningTimer._id);
      const stopped = res.data as StoppedTimerEntry;
      setRunningTimer(null);
      setStoppedEntry(stopped);
      return stopped;
    } finally {
      setLoading(false);
    }
  }, [runningTimer]);

  // ── Update running timer (assign matter mid-run) ──────────────

  const updateRunningTimer = useCallback(
    async (data: { matterId?: string; clientId?: string; narrative?: string }) => {
      if (!runningTimer) return;
      setLoading(true);
      try {
        const res = await timeEntriesApi.update(runningTimer._id, data);
        // Re-fetch to get populated matter/client data
        await fetchRunning();
      } finally {
        setLoading(false);
      }
    },
    [runningTimer, fetchRunning]
  );

  // ── Update stopped entry ───────────────────────────────────────────

  const updateStoppedEntry = useCallback(
    async (
      id: string,
      data: { minutes?: number; narrative?: string; rateCents?: number }
    ) => {
      setLoading(true);
      try {
        await timeEntriesApi.update(id, data);
        setStoppedEntry((prev) => (prev ? { ...prev, ...data } : null));
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ── Dismiss stopped entry review ──────────────────────────────────

  const dismissStoppedEntry = useCallback(() => {
    setStoppedEntry(null);
  }, []);

  return (
    <RunningTimerContext.Provider
      value={{
        runningTimer,
        stoppedEntry,
        elapsedSeconds,
        startTimer,
        stopTimer,
        updateRunningTimer,
        updateStoppedEntry,
        dismissStoppedEntry,
        loading,
        refresh: fetchRunning,
      }}
    >
      {children}
      <FloatingTimer />
    </RunningTimerContext.Provider>
  );
};
